import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import wwllogo from "../../assets/icons/wwllogo.webp";
import {
  addUserMessage,
  startAssistantMessage,
  beginSending,
  openChatStream,
  cancelStream,
} from "../../redux/chat/chatSlice.js";
import { hasChatAccess } from "../../redux/user/userSlice";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Button from "../../components/Button.js";
import GoProModal from "../../components/Modals/GoProModal.js";

export default function AIAssistant() {
  const dispatch = useDispatch();
  const { messages, sending } = useSelector((s) => s.chat);
  const user = useSelector((s) => s.user?.currentUser);
  const hasAssistantPermission = useSelector(hasChatAccess);

  const [input, setInput] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const endRef = useRef(null);

  const inputRef = useRef(null);
  const isInputFocusedRef = useRef(false);

  // ✅ prevents modal from instantly reopening after close
  const modalCooldownRef = useRef(false);
  const modalCooldownTimeoutRef = useRef(null);

  const isIOS =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !window.MSStream;

  const [kb, setKb] = useState(0);

  const compactMarkdown = (text = "") => {
    let t = String(text).replace(/\r\n/g, "\n");
    t = t.replace(/\u00A0/g, " ");
    t = t.replace(/[ \t]+$/gm, "");
    t = t.replace(/\n{3,}/g, "\n\n");
    t = t.replace(/:\n\n+/g, ":\n\n");
    return t.trim();
  };

  const openUpgradeModal = () => {
    if (modalCooldownRef.current) return;
    if (modalOpen) return;
    setModalOpen(true);
  };

  const closeUpgradeModal = () => {
    setModalOpen(false);

    // block immediate reopen from click-through / focus bounce
    modalCooldownRef.current = true;

    if (modalCooldownTimeoutRef.current) {
      clearTimeout(modalCooldownTimeoutRef.current);
    }

    modalCooldownTimeoutRef.current = setTimeout(() => {
      modalCooldownRef.current = false;
    }, 250);
  };

  useEffect(() => {
    return () => {
      if (modalCooldownTimeoutRef.current) {
        clearTimeout(modalCooldownTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const prevHtmlOverflowX = document.documentElement.style.overflowX;
    const prevBodyOverflowX = document.body.style.overflowX;

    document.documentElement.style.overflowX = "hidden";
    document.body.style.overflowX = "hidden";

    return () => {
      document.documentElement.style.overflowX = prevHtmlOverflowX;
      document.body.style.overflowX = prevBodyOverflowX;
    };
  }, []);

  useEffect(() => {
    const prevHtmlOverflowY = document.documentElement.style.overflowY;
    const prevBodyOverflowY = document.body.style.overflowY;

    document.documentElement.style.overflowY = "hidden";
    document.body.style.overflowY = "hidden";

    return () => {
      document.documentElement.style.overflowY = prevHtmlOverflowY;
      document.body.style.overflowY = prevBodyOverflowY;
    };
  }, []);

  useEffect(() => {
    if (!isIOS) return;

    const vv = window.visualViewport;
    if (!vv) return;

    let rafId = null;

    const computeKb = () =>
      Math.max(0, window.innerHeight - vv.height - vv.offsetTop);

    const update = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setKb(computeKb());
      });
    };

    const onFocus = () => {
      setTimeout(update, 50);
    };

    const el = inputRef.current;
    el?.addEventListener("focus", onFocus);

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);

    return () => {
      el?.removeEventListener("focus", onFocus);
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isIOS]);

  useEffect(() => {
    if (isInputFocusedRef.current) return;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const handleSend = async (e) => {
    e.preventDefault();

    if (!hasAssistantPermission) {
      openUpgradeModal();
      return;
    }

    const trimmed = input.trim();
    if (!trimmed || sending) return;

    const conversationForServer = [
      ...messages.map((m) => ({
        role: m.role,
        content: m.text || "",
      })),
      { role: "user", content: trimmed },
    ];

    dispatch(addUserMessage(trimmed));
    dispatch(startAssistantMessage());
    dispatch(beginSending());
    setInput("");

    dispatch(openChatStream(conversationForServer));
  };

  const getInitials = (u) => {
    if (!u) return "U";

    const first = u.firstName ?? u.first_name;
    const last = u.lastName ?? u.last_name;

    if (first || last) {
      return `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase() || "U";
    }

    const full = u.name ?? u.fullName ?? u.displayName ?? u.username ?? u.email;

    if (typeof full === "string" && full.trim()) {
      const safe = full.includes("@") ? full.split("@")[0] : full;
      const parts = safe.split(/[\s._-]+/).filter(Boolean);

      if (parts.length === 1) return parts[0][0].toUpperCase();

      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }

    return "U";
  };

  const isRecipeTitle = (text) =>
    /^[A-Z][A-Za-z\s]+(?:Bake|Casserole|Salad|Empanadas|Oats|Pan|Recipe)/.test(
      text.trim()
    );

  const shouldExpandAssistantBubble = (text = "") => {
    const t = String(text);
    const longByChars = t.length > 280;
    const longByLines = t.split("\n").length > 8;
    const hasList = /(^|\n)\s*([-*+]|\d+\.)\s+/m.test(t);
    const hasMultipleParagraphs = /\n\s*\n/.test(t);

    return longByChars || longByLines || hasList || hasMultipleParagraphs;
  };

  return (
    <div className="fixed inset-0 pt-20 w-full overflow-hidden overflow-x-clip overscroll-none overscroll-x-none bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 md:relative md:inset-auto md:h-full md:pt-0">
      <div className="h-full w-full max-w-full overflow-hidden overflow-x-clip overscroll-none overscroll-x-none touch-pan-y grid grid-rows-[auto,minmax(0,1fr),auto]">
        <header className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-zinc-200 bg-white/90 px-4 py-3 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/80">
          <div className="flex items-center gap-2">
            <span aria-hidden className="text-lg">🍽️</span>
            <h1 className="text-base font-semibold tracking-tight">
              Workweek Lunch GPT
            </h1>
          </div>
        </header>

        <main className="min-h-0 h-full mx-auto w-full max-w-4xl max-w-full overflow-x-clip px-4 py-4 sm:px-6">
          <section
            className="h-full min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y rounded-2xl border border-zinc-200 bg-zinc-100 p-3 dark:border-zinc-800 dark:bg-zinc-900"
            style={{ WebkitOverflowScrolling: "touch" }}
            aria-live="polite"
            aria-busy={sending}
          >
            {messages.map((m) => {
              const isAssistant = m.role === "assistant";
              const expandAssistant = isAssistant && shouldExpandAssistantBubble(m.text);

              return (
                <article
                  key={m.id}
                  className="mb-2 min-w-0 grid grid-cols-[36px,1fr] items-start gap-2"
                >
                  <div
                    className={`grid h-9 w-9 mt-1 place-items-center rounded-full border text-sm font-semibold ${
                      isAssistant
                        ? "border-zinc-200 bg-white text-zinc-900 dark:border-zinc-800 dark:bg-zinc-800 dark:text-zinc-100"
                        : "border-wwlOrange/40 bg-wwlOrange/5 text-wwlOrange"
                    }`}
                    aria-hidden
                  >
                    {isAssistant ? (
                      <img
                        src={wwllogo}
                        alt="WWL Logo"
                        className="h-full w-full rounded-full object-cover scale-[1.2]"
                      />
                    ) : (
                      getInitials(user)
                    )}
                  </div>

                  <div
                    className={`min-w-0 whitespace-normal break-words [overflow-wrap:anywhere] rounded-xl border px-3 py-2 leading-relaxed ${
                      isAssistant
                        ? `border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-800 ${
                            expandAssistant ? "w-full" : "inline-block w-fit max-w-[85%]"
                          }`
                        : "inline-block w-fit max-w-[85%] border-zinc-300 bg-transparent dark:border-zinc-700"
                    }`}
                  >
                    {m.text ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({ node, ...props }) => (
                            <a
                              {...props}
                              className="text-wwlOrange underline underline-offset-2 hover:opacity-80"
                            />
                          ),
                          p: ({ node, children, ...props }) => {
                            const text = Array.isArray(children)
                              ? children.join("")
                              : String(children);
                            const isTitle = isRecipeTitle(text);

                            return (
                              <p
                                {...props}
                                className={`leading-relaxed ${
                                  isTitle ? "mt-3 mb-1 font-medium" : "my-1"
                                } first:mt-0 last:mb-0`}
                              >
                                {children}
                              </p>
                            );
                          },
                          ul: ({ node, ...props }) => (
                            <ul {...props} className="my-2 ml-5 list-disc space-y-1" />
                          ),
                          ol: ({ node, ...props }) => (
                            <ol {...props} className="my-2 ml-5 list-decimal space-y-1" />
                          ),
                          li: ({ node, ...props }) => (
                            <li {...props} className="my-0 [&>p]:my-0" />
                          ),
                        }}
                      >
                        {compactMarkdown(m.text)}
                      </ReactMarkdown>
                    ) : (
                      <div
                        className="flex items-center gap-2 py-2 pr-2"
                        aria-label="Assistant is typing"
                      >
                        <span className="block h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.2s]" />
                        <span className="block h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400" />
                        <span className="block h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:0.2s]" />
                      </div>
                    )}
                  </div>
                </article>
              );
            })}

            <div ref={endRef} />
          </section>
        </main>

        <div style={{ paddingBottom: kb }} className="bg-white dark:bg-zinc-900">
          <form
            onSubmit={handleSend}
            className="grid grid-cols-[1fr,auto] gap-2 border-t border-zinc-200 bg-white px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] dark:border-zinc-800 dark:bg-zinc-900 sm:px-6"
          >
            <input
              ref={inputRef}
              type="text"
              value={hasAssistantPermission ? input : ""}
              onChange={(e) => {
                if (!hasAssistantPermission) return;
                setInput(e.target.value);
              }}
              onMouseDown={(e) => {
                if (!hasAssistantPermission) {
                  e.preventDefault();
                  openUpgradeModal();
                }
              }}
              onFocus={() => {
                isInputFocusedRef.current = true;
              }}
              onBlur={() => {
                isInputFocusedRef.current = false;
              }}
              autoComplete="off"
              readOnly={!hasAssistantPermission}
              placeholder={
                hasAssistantPermission
                  ? "Type your question here"
                  : "Upgrade your membership to use the AI chatbot"
              }
              aria-label="Message"
              className="
                w-full min-w-0 rounded-xl
                border border-zinc-300
                bg-zinc-100
                px-4 py-3
                text-[16px] md:text-[15px]
                text-zinc-900
                outline-none
                placeholder:text-zinc-500
                focus:border-wwlOrange
                focus:ring-2 focus:ring-emerald-500/20
                dark:border-zinc-700
                dark:bg-zinc-800
                dark:text-zinc-100
                dark:placeholder:text-zinc-400
              "
            />

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => dispatch(cancelStream())}
                disabled={!sending}
                className="hidden sm:inline rounded-xl border border-zinc-300 px-4 py-3 text-[15px] dark:border-zinc-700 disabled:opacity-50"
                title="Cancel"
              >
                Stop
              </button>

              {hasAssistantPermission ? (
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  className="rounded-xl border border-zinc-300 bg-wwlOrange px-4 py-3 text-[15px] font-medium text-white transition focus:outline-none focus:ring-2 focus:ring-wwlOrange enabled:hover:bg-wwlTransparentOrange disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send"}
                </button>
              ) : (
                <Button
                  btnText="Unlock AI Chat"
                  extraClasses="border rounded-xl bg-wwlOrange text-wwlWhite border-transparent hover:border-wwlOrange hover:bg-wwlWhite hover:text-wwlOrange py-3 px-4 text-[15px] transition-colors duration-300"
                  onClick={openUpgradeModal}
                />
              )}
            </div>
          </form>
        </div>

        <GoProModal setOpen={closeUpgradeModal} open={modalOpen} />
      </div>
    </div>
  );
}